import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    mapCatalogueSearchScope,
    SCOPE_CATALOGUE_OBJECT,
    SCOPE_EPRESS_OBJECT,
    setTopHitLabel
} from '../../store/search/actions';
import { createSelectionlist, setActiveList } from '../../store/orderlist/actions';
import historyUtils, { modifyURLState, modifyURLStates } from '../utils/utils-routing';
import { DisplayTypeState } from '../../store/displayType/state';
import { Orderlist } from '../../../models/OrderList';
import { ProductSorting } from '../../../models/Product';
import { PriceTier } from '../../../models/PriceTier';
import { LoanFormatState } from '../../store/loanFormat/state';
import { SearchState } from '../../store/search/state';
import { AppState } from '../../store/appState';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import { AppDispatch } from '../../store';
import { invalidateSearchResultProducts, useSearchResultProducts } from './api/useSearchResultProducts';
import queryString from 'query-string';
import { useQueryParams } from '../hooks/useQueryParams';
import { deserializeFilter } from '../utils/deserializeFilter';
import { getLoanFormat } from '../utils/getLoanFormat';
import { ContainerInnerWithColumns } from '../common/ContainerInner';
import { LoadingWrapper } from '../components/LoadingWrapper';
import LoadingOverlay from '../components/LoadingOverlay';
import SearchResultHeader from './SearchResultHeader';
import { BulkActionButtons } from './BulkActionButtons';
import FilterBar from '../common/FilterBar';
import { SEARCH_RESULT_SORT_BY } from '../common/SortByDropDown';
import ProductList from '../common/ProductList';
import ListItem from '../common/ListItems/ListItem';
import Pagination, { getPaginationStateObject } from '../common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../components/NoElementsFound';
import { SearchResultSidebar } from './SearchResultSidebar';
import { useOrderLists } from '../common/useOrderLists';
import { getConcreteLoanFormatStates } from '../../store/loanFormat/actions';
import { deriveSearchScopeFromPathname } from './utils/deriveSearchScopeFromPathname';
import { ResponsiveFilterBar } from '../filter/ResponsiveFilterBar';
import { fetchCatalogueSearchAggregationsQuickSearch } from '../../api/catalogue-search/fetchCatalogueSearchAggregationsQuickSearch';
import { useLibraryManagedProductGroups } from '../../yourborrowbox/marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { QueryKeyBuilder } from '../filter/useAggregationsQuery';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { fetchCatalogueSearchAggregations } from '../../api/catalogue-search/fetchCatalogueSearchAggregations';
import { defaultVisibleFilterGroups } from '../filter/defaultVisibleFilterGroups';
import { CollectionProductTile } from '../../yourborrowbox/collection/CollectionProductTile';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useAddFromCatalogueSearchToCart } from '../../api/shopping-cart/useAddFromCatalogueSearchToCart';
import { useAddFromCatalogueSearchToList } from '../../api/selection-list/useAddFromCatalogueSearchToList';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../common/useListContentChecks';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { useAddProductToGroup } from '../../api/productGroups/useAddProductToGroup';
import { SubscriptionPeriodType } from '../../../models/Article';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';
import { ProductSelectionHeader } from '../product-selection/ProductSelectionHeader';
import { CatalogueMenuActionButtons } from '../../shop/catalogue/CatalogueMenuActionButtons';
import { nothingSelected } from '../product-selection/ProductSelectionUtils';
import { MAX_SELECT, useProductSelection } from '../product-selection/useProductSelection';
import { ExcludeProductSelection } from '../product-selection/ProductSelectionTypes';
import { showNotification, TYPE_WARNING } from '../../store/notifications/actions';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
const DEFAULT_PAGE_SIZE = 54;

export const SearchResultPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const history = useHistory();
    const router = useRouteMatch();
    const authentication = useSelector((state: AppState) => state.authentication);
    const productGroups = useLibraryManagedProductGroups();
    const { orderlists } = useOrderLists();
    const isInCollection = location.pathname.includes('collection');
    const [loanFormats, setLoanFormats] = useState<LoanFormatState[]>([]);

    const { productSelection, toggleSelectionMode, onSelectButtonClick, selectAll, cancelSelection } =
        useProductSelection();

    useEffect(() => {
        setLoanFormats(getConcreteLoanFormatStates());
    }, []);

    const { loanFormat: loanFormatPathParam } = useParams<{ loanFormat?: string }>();
    const loanFormat = getLoanFormat(loanFormatPathParam);
    const { displayFilter: displayFilterQueryParam } = queryString.parse(location.search);
    const searchScope = deriveSearchScopeFromPathname(location.pathname);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display },
        searchLabel: { value: searchLabel },
        scope: { value: scopeQueryParam },
        refine: { value: refine },
        q: { value: q },
        shopLanguage: { value: shopLanguage }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        display: 'string',
        searchLabel: 'string',
        refine: 'string',
        q: 'string',
        scope: 'string',
        shopLanguage: 'string'
    });

    const scope = scopeQueryParam?.toUpperCase() ?? SCOPE_CATALOGUE_OBJECT.requestKey;
    const loanFormatName = loanFormats.find(({ key }) => key === scope)?.name.toLowerCase();
    const loanFormatValue = loanFormatName
        ? t(`loanFormat.plain.${loanFormatName}`, { count: 10 })
        : t('shop.orders.items');

    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEVANCE;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);
    const searchQuery = q ?? undefined;

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return [
                'searchResultPage',
                'aggregations',
                scope,
                searchQuery,
                refine,
                filters,
                expandedFilterGroups,
                shopLanguage
            ];
        },
        [scope, searchQuery, refine, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchCatalogueSearchAggregations({ ...options, scope, query: searchQuery, refine, shopLanguage }),
        [refine, scope, searchQuery, shopLanguage]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchCatalogueSearchAggregationsQuickSearch({
                ...options,
                scope,
                query: searchQuery,
                refine,
                shopLanguage
            }),
        [refine, scope, searchQuery, shopLanguage]
    );
    //endregion

    useEffect(() => {
        if (searchLabel) {
            dispatch(setTopHitLabel(decodeURIComponent(searchLabel)));
        }
        if (q) {
            dispatch(setTopHitLabel(decodeURIComponent(q)));
        }
    }, [dispatch, q, searchLabel]);

    const {
        products,
        count,
        isSearchResultsLoading,
        currentRefine,
        didYouMean,
        relatedResults,
        refinedSearchCategories
    } = useSearchResultProducts({
        filterMap: filter,
        pagination: { sortBy, page, pageSize },
        q: searchQuery,
        loanFormat: loanFormat.requestKey,
        scope,
        refine,
        shopLanguage
    });

    function onUnsetRefineSearch() {
        const refineQueryState = {
            name: 'refine',
            value: undefined,
            encode: false
        };

        setPage(0);
        historyUtils.replaceURLSearchStates([refineQueryState]);
    }

    function onRefineSearch(refine: string) {
        const refineQueryState = {
            name: 'refine',
            value: refine,
            encode: false
        };

        setPage(0);

        historyUtils.replaceURLSearchStates([refineQueryState]);
    }

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateSearchResultProducts().then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const { isLoading: isLoadingAddProductsToCartByFilter, addFromSearchToCartBulk } = useAddFromCatalogueSearchToCart({
        filter: filterQueryParam ?? '',
        scope: mapCatalogueSearchScope(scope),
        query: q ?? '',
        refine,
        shopLanguage,
        onSuccess: () => {
            invalidateSearchResultProducts().then();
            cancelSelection();
        }
    });

    const { isLoading: isLoadingAddProductsToSelectionListByFilter, addFromSearchToListBulk } =
        useAddFromCatalogueSearchToList({
            filter: filterQueryParam ?? '',
            scope: mapCatalogueSearchScope(scope),
            query: q ?? '',
            refine,
            shopLanguage,
            onSuccess: () => {
                cancelSelection();
            }
        });

    const addProductToGroup = useAddProductToGroup();

    const onBulkAddToList = (
        list: Orderlist,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: count
        };

        if (productSelection.totalCount > MAX_SELECT) {
            dispatch(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
            throw new Error('max selection exceeded');
        }

        return addFromSearchToListBulk(
            list.id,
            productSelection,
            priceTier,
            subscriptionPeriodType,
            performChecks
        ).then(data => {
            if (!performChecks) {
                dispatch(setActiveList(list));
            }
            return data;
        });
    };

    const onBulkAddToCart = (
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: count
        };

        if (productSelection.totalCount > MAX_SELECT) {
            dispatch(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
            throw new Error('max selection exceeded');
        }

        return addFromSearchToCartBulk(productSelection, priceTier, subscriptionPeriodType, performChecks);
    };

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onCreateNewListClicked(newSelectionListName: string) {
        dispatch(createSelectionlist(newSelectionListName));
    }

    const searchObj: Pick<SearchState, 'q'> = {
        q: searchQuery
    };

    const isLoading =
        isSearchResultsLoading || isLoadingAddProductsToCartByFilter || isLoadingAddProductsToSelectionListByFilter;

    return (
        <ContainerInnerWithColumns className="search-results" isLoading={isLoading}>
            {isLoading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={isLoading} />
                </LoadingWrapper>
            ) : null}

            <div className="search-results-header-bar" key="search-results-header-bar">
                <SearchResultHeader
                    didYouMean={didYouMean}
                    searchScope={searchScope}
                    location={location}
                    onRefineSearch={onRefineSearch}
                    onUnsetRefineSearch={onUnsetRefineSearch}
                    refinedSearchCategories={refinedSearchCategories}
                    currentRefine={currentRefine}
                />
            </div>
            <div className="search-results-content flex-ca" key="search-results-content">
                <div style={{ width: '100%', paddingRight: '20px' }}>
                    <ProductSelectionHeader
                        header={
                            <div
                                className="display-flex"
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: '62px'
                                }}
                            >
                                <h4>{loanFormatValue}</h4>
                                {products.length > 0 || isLoading ? (
                                    <BulkActionButtons
                                        listLabel={t('components.searchResults.addAllResultsToList')}
                                        cartLabel={t('components.searchResults.addAllResultsToCart')}
                                        orderlists={orderlists}
                                        onCreateNewListClicked={onCreateNewListClicked}
                                        onBulkAddToList={onBulkAddToList}
                                        onBulkAddToCart={onBulkAddToCart}
                                        onlyEpressProducts={scope === SCOPE_EPRESS_OBJECT.requestKey}
                                    />
                                ) : null}
                            </div>
                        }
                        menu={
                            <CatalogueMenuActionButtons
                                onBulkAddToCart={(priceTier, subscriptionPeriodType, performChecks) =>
                                    addFromSearchToCartBulk(
                                        productSelection,
                                        priceTier,
                                        subscriptionPeriodType,
                                        performChecks
                                    )
                                }
                                onBulkAddToList={(orderList, priceTier, subscriptionPeriodType, performChecks) =>
                                    addFromSearchToListBulk(
                                        orderList.id,
                                        productSelection,
                                        priceTier,
                                        subscriptionPeriodType,
                                        performChecks
                                    )
                                }
                                isNothingSelected={nothingSelected(productSelection)}
                                defaultPriceTier={
                                    scope === SCOPE_EPRESS_OBJECT.requestKey
                                        ? { lendingModel: 'SIM', level: 'MIN' }
                                        : { lendingModel: 'OCOU', level: 'MIN' }
                                }
                                onlyEpressProducts={scope === SCOPE_EPRESS_OBJECT.requestKey}
                            />
                        }
                    />
                    <FilterBar
                        totalProducts={count}
                        onSortChange={setSortBy}
                        dispatch={dispatch}
                        currentPage={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        hideGroupBy={true}
                        displayType={display as DisplayTypeState}
                        sortBy={sortBy}
                        onDisplayChange={onDisplayChange}
                        hideSelectButtons={false}
                        onFilterDisplayToggle={onFilterDisplayToggle}
                        sorting={SEARCH_RESULT_SORT_BY}
                        displayFilter={displayFilter}
                        productSelection={productSelection}
                        toggleSelectionMode={toggleSelectionMode}
                        selectAll={() => selectAll(count)}
                    />
                    <div className="sr-flex-content" style={{ alignItems: 'flex-start' }}>
                        {displayFilter ? (
                            <ResponsiveFilterBar
                                visibleFilterGroups={visibleFilterGroups}
                                buildQueryKey={aggregationsQueryKeyBuilder}
                                fetchAggregations={fetchAggregations}
                                fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                            />
                        ) : null}
                        {products.length > 0 || isLoading ? (
                            <div className={classNames('ca-main', { 'min-h-screen': isLoading })}>
                                <div className="ca-main-inner">
                                    <ProductList
                                        orderlists={orderlists}
                                        products={products}
                                        onAddPrice={changePrice}
                                        totalProducts={count}
                                        displayType={display}
                                        listItemComponent={isInCollection ? ListItem : undefined}
                                        router={router}
                                        history={history}
                                        tileItemComponent={isInCollection ? CollectionProductTile : undefined}
                                        onAddToGroup={addProductToGroup}
                                        productGroups={productGroups}
                                        currentPage={page}
                                        isProductSelectable={true}
                                        productSelection={productSelection}
                                        onSelectProduct={onSelectButtonClick}
                                        useOnlyProductIdForSelectButtonCheck={true}
                                    />
                                    <Pagination
                                        totalItems={count}
                                        onPageChange={setPage}
                                        currentPage={page}
                                        pageSize={pageSize}
                                    />
                                </div>
                            </div>
                        ) : (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                        )}
                    </div>
                </div>
                {!displayFilter ? (
                    <SearchResultSidebar related={relatedResults} search={searchObj} shopLanguage={shopLanguage} />
                ) : null}
            </div>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </ContainerInnerWithColumns>
    );
};
